<script setup>
import { computed } from 'vue'
import { RouterLink, useRouter, useRoute } from 'vue-router'
import CardLichen from '@/design/primitives/CardLichen.vue'
import CoinGlyph from '@/design/primitives/CoinGlyph.vue'
import ButtonSeed from '@/design/primitives/ButtonSeed.vue'
import { useCartStore } from '@/stores/cartStore'
import { useToastStore } from '@/stores/toastStore'
import { getToken } from '@/api/client'

const props = defineProps({
  id:          { type: [String, Number], required: true },
  kind:        { type: String, required: true }, // 'product' | 'item' | 'creature' | 'bundle'
  name:        { type: String, default: '' },
  description: { type: String, default: '' },
  price:       { type: Number, default: 0 },
  imageUrl:    { type: String, default: '' },
  tag:         { type: String, default: '' }  // 'BUNDLE' | 'ITEM' | 'DINO' | 'NEW' | 'LIMITED' | ''
})

const cart   = useCartStore()
const toast  = useToastStore()
const router = useRouter()
const route  = useRoute()

const inCart = computed(() =>
  cart.items.some(i => i.id === props.id && i.kind === props.kind)
)

function addToCart() {
  // Purchases require an account — gate the cart on login.
  // Guests are sent straight to the sign-in form and returned here after.
  if (!getToken()) {
    router.push({ name: 'register', query: { tab: 'signin', next: route.fullPath } })
    return
  }
  cart.add({
    id:       props.id,
    kind:     props.kind,
    name:     props.name,
    price:    props.price,
    imageUrl: props.imageUrl,
    qty:      1
  })
  toast.show(
    `${props.name} added · ${cart.count} in cart`,
    { tone: 'success', duration: 2500 }
  )
}

function formatPrice(val) {
  if (!val && val !== 0) return '—'
  // Show cents only when the price actually has them (e.g. 9.99 → "9.99",
  // 10 → "10"). Matches the cart row formatting (CargoRow).
  return Number(val).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })
}
</script>

<template>
  <CardLichen class="poster" :hoverable="true">
    <!-- Tag pill — top-left corner, shown only when tag is set -->
    <div v-if="tag" class="poster__tag u-display u-allcaps" aria-label="Category tag">
      {{ tag }}
    </div>

    <!-- Full-card image area: 4/5 aspect -->
    <div class="poster__image-wrap" aria-hidden="true">
      <img
        v-if="imageUrl"
        :src="imageUrl"
        :alt="name"
        class="poster__image"
        loading="lazy"
      />
      <div v-else class="poster__image-placeholder" />

      <!-- Bottom dark gradient overlay -->
      <div class="poster__overlay" />

      <!-- Content that lives on top of the image overlay -->
      <div class="poster__plate">
        <!-- Title -->
        <p class="poster__title u-display u-allcaps">{{ name }}</p>

        <!-- Description — truncated single line -->
        <p v-if="description" class="poster__desc">{{ description }}</p>

        <!-- Price + CTA row -->
        <div class="poster__road">
          <span class="poster__price">
            <CoinGlyph :size="16" />
            <span class="poster__price-value u-mono u-gold">{{ formatPrice(price) }}</span>
          </span>
          <div class="poster__cta">
            <ButtonSeed size="sm" variant="primary" @click.stop="addToCart">ADD</ButtonSeed>
            <RouterLink v-if="inCart" to="/cart" class="poster__cart-link" @click.stop>
              <ButtonSeed size="sm" variant="ghost">CART</ButtonSeed>
            </RouterLink>
          </div>
        </div>
      </div>
    </div>
  </CardLichen>
</template>

<style scoped>
/* ── Poster card — overrides CardLichen defaults ── */
.poster {
  /* Remove CardLichen's default padding; image must bleed to edges */
  padding: 0 !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* ── Tag pill ── */
.poster__tag {
  position: absolute;
  top: var(--sp-3);
  left: var(--sp-3);
  z-index: 10;
  background: var(--copper);
  color: var(--ink-inverse);
  font-size: var(--fs-12);
  padding: 3px 8px;
  border-radius: var(--r-xs);
  line-height: 1.4;
  pointer-events: none;
}

/* ── Image container — 21:9 cinematic banner (very compact) ── */
.poster__image-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 21 / 9;
  overflow: hidden;
  flex-shrink: 0;
}

.poster__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
  display: block;
  transition: transform var(--t-slow) var(--ease-cinema);
}

/* Scale image on card hover (parent CardLichen handles translateY + border) */
.poster:hover .poster__image {
  transform: scale(1.04);
}

/* Amber-gold gradient placeholder when no image.
   The mid-stops use --gold-soft layered over --bg-deep to
   approximate a warm amber tone without hardcoded hex. */
.poster__image-placeholder {
  width: 100%;
  height: 100%;
  background:
    linear-gradient(160deg,
      var(--bg-deep) 0%,
      var(--gold-soft) 45%,
      transparent 100%),
    var(--bg-deep);
}

/* ── Dark gradient overlay — bottom 40% ── */
.poster__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(11, 10, 8, 0.96) 0%,
    rgba(11, 10, 8, 0.82) 28%,
    rgba(11, 10, 8, 0.30) 56%,
    transparent 80%
  );
  pointer-events: none;
}

/* ── Plate — content inside overlay ── */
.poster__plate {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: var(--sp-3) var(--sp-3) var(--sp-4);
  display: flex;
  flex-direction: column;
  gap: 5px;
}

/* Title */
.poster__title {
  margin: 0;
  font-size: var(--fs-21);
  color: var(--ink);
  line-height: var(--lh-snug);
  /* Clamp to 2 lines */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Description */
.poster__desc {
  margin: 0;
  font-family: var(--font-body);
  font-size: var(--fs-12);
  color: var(--ink-soft);
  line-height: var(--lh-snug);
  /* Single line truncation */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Price + CTA row */
.poster__road {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-2);
  margin-top: var(--sp-1);
}
.poster__cta {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
}
.poster__cart-link { display: inline-flex; }

.poster__price {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
}

.poster__price-value {
  font-size: var(--fs-16);
  line-height: 1;
}
</style>
